import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Assessment,
  MusicNote,
  BoltOutlined,
  TrendingUp,
} from '@mui/icons-material';
import { useAICoverage, useAIStats } from '../../hooks/useAIGeneration';
import { useOrgFilter } from '../../contexts/OrganizationContext';

const SONGFORGE_ACCENT_COLOR = '#8b5cf6';
const MONTHLY_GOAL = 200; // Configurable monthly generation goal

export const CoverageSection: React.FC = () => {
  const orgFilter = useOrgFilter();

  const { data: coverage, isLoading: loadingCoverage, error: coverageError } = useAICoverage(orgFilter);
  const { data: stats, isLoading: loadingStats, error: statsError } = useAIStats(orgFilter);

  if (loadingCoverage || loadingStats) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (coverageError || statsError) {
    return (
      <Alert severity="error">
        Failed to load analytics: {((coverageError || statsError) as Error)?.message}
      </Alert>
    );
  }

  // Calculate monthly progress
  const currentMonth = new Date().toISOString().slice(0, 7);
  const thisMonthData = stats?.by_month?.find((m) => m.month === currentMonth);
  const thisMonthCount = thisMonthData?.count || 0;
  const progressPercent = Math.min((thisMonthCount / MONTHLY_GOAL) * 100, 100);

  // Get top genres and moods
  const topGenres = coverage?.by_genre?.slice(0, 8) || [];
  const topMoods = coverage?.by_mood?.slice(0, 8) || [];
  const energyDist = coverage?.by_energy || [];

  return (
    <Box>
      {/* Stats Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <MusicNote color="primary" />
                <Typography color="text.secondary" variant="body2">
                  Total in Queue
                </Typography>
              </Box>
              <Typography variant="h4">{stats?.total_in_queue || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <TrendingUp color="success" />
                <Typography color="text.secondary" variant="body2">
                  Success Rate
                </Typography>
              </Box>
              <Typography variant="h4">{stats?.success_rate || 0}%</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Assessment color="info" />
                <Typography color="text.secondary" variant="body2">
                  References
                </Typography>
              </Box>
              <Typography variant="h4">{stats?.reference_count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BoltOutlined color="warning" />
                <Typography color="text.secondary" variant="body2">
                  Templates
                </Typography>
              </Box>
              <Typography variant="h4">{stats?.template_count || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Monthly Progress */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Monthly Generation Progress
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <LinearProgress
            variant="determinate"
            value={progressPercent}
            sx={{ flexGrow: 1, height: 12, borderRadius: 1 }}
            color={progressPercent >= 100 ? 'success' : 'primary'}
          />
          <Typography variant="body2" sx={{ minWidth: 100 }}>
            {thisMonthCount} / {MONTHLY_GOAL}
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary">
          {progressPercent >= 100
            ? 'Goal reached! Keep going to build your library.'
            : `${MONTHLY_GOAL - thisMonthCount} more songs to reach this month's goal`}
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Genre Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Coverage by Genre
            </Typography>
            {topGenres.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {topGenres.map((item) => (
                  <Box key={item.genre} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 100 }}>
                      {item.genre || 'Unknown'}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(item.count / (topGenres[0]?.count || 1)) * 100}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No AI-generated content yet. Start generating to see coverage.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Mood Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Coverage by Mood
            </Typography>
            {topMoods.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {topMoods.map((item) => (
                  <Box key={item.mood} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" sx={{ minWidth: 100 }}>
                      {item.mood || 'Unknown'}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={(item.count / (topMoods[0]?.count || 1)) * 100}
                      sx={{ flexGrow: 1, height: 8, borderRadius: 1 }}
                      color="secondary"
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No AI-generated content yet. Start generating to see coverage.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Energy Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Energy Level Distribution
            </Typography>
            {energyDist.length > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', height: 150 }}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((level) => {
                  const item = energyDist.find((e) => e.energy_level === level);
                  const count = item?.count || 0;
                  const maxCount = Math.max(...energyDist.map((e) => e.count), 1);
                  const height = (count / maxCount) * 100;
                  return (
                    <Tooltip key={level} title={`Energy ${level}: ${count} songs`}>
                      <Box
                        sx={{
                          width: '8%',
                          height: `${Math.max(height, 5)}%`,
                          bgcolor: count > 0 ? SONGFORGE_ACCENT_COLOR : 'grey.300',
                          borderRadius: '4px 4px 0 0',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 0.8 },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No energy data available yet.
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption" color="text.secondary">Low (1)</Typography>
              <Typography variant="caption" color="text.secondary">Medium (5)</Typography>
              <Typography variant="caption" color="text.secondary">High (10)</Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Status Breakdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Queue Status
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {stats?.by_status?.map((item) => (
                <Chip
                  key={item.status}
                  label={`${item.status}: ${item.count}`}
                  color={
                    item.status === 'uploaded' ? 'success' :
                    item.status === 'failed' ? 'error' :
                    item.status === 'generated' ? 'warning' :
                    item.status === 'prompt_ready' ? 'primary' : 'default'
                  }
                />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Quality Ratings */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Quality Ratings Distribution
            </Typography>
            {stats?.by_rating && stats.by_rating.length > 0 ? (
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                {[1, 2, 3, 4, 5].map((rating) => {
                  const item = stats.by_rating?.find((r) => r.quality_rating === rating);
                  const count = item?.count || 0;
                  return (
                    <Card key={rating} variant="outlined" sx={{ minWidth: 100, textAlign: 'center' }}>
                      <CardContent>
                        <Typography variant="h4">{count}</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                          {Array.from({ length: rating }).map((_, i) => (
                            <Typography key={i} color="warning.main">*</Typography>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No quality ratings yet. Rate your generated content to see distribution.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CoverageSection;
